/**
 * Detail panel — displays full information for a selected event.
 * Triggered by event:selected on the event bus.
 * Shows header, severity, meta, description, location, tags, links,
 * entities stub, and related events within 50km / 24h.
 */

import { appStore } from '@/core/store';
import { eventBus } from '@/core/events';
import type { NormalizedEvent } from '@/sources/base-source';
import type { FocalPoint } from '@/intelligence/analysis/types';
import { sanitizeUrl } from '@/utils/sanitize';
import { haversineDistance } from '@/utils/geo';
import { formatUTC, relativeTime } from '@/utils/time';
import { SEVERITY_COLORS } from '@/utils/severity';
import { createCompactEventItem } from '@/ui/components/compact-event-item';
import { getMap } from '@/map/engine';
import { summarizeEvent } from '@/intelligence/ai/summarizer';
import { getAvailableProviderIds } from '@/intelligence/ai/provider-registry';
import { findRelatedAssets, ASSET_CATEGORY_LABELS } from '@/intelligence/analysis/related-assets';
import type { NearbyAsset } from '@/intelligence/analysis/related-assets';
import { calculatePopulationExposure, preloadPopulationGrid } from '@/intelligence/analysis/population-exposure';

/** Type icon labels (simple text markers). */
const TYPE_ICONS: Record<string, string> = {
  conflict: '\u2694',     // crossed swords
  protest: '\u270A',      // raised fist
  environmental: '\u26A0',     // warning
  military: '\u2B50',     // star
  fire: '\uD83D\uDD25',  // (text fallback)
  earthquake: '\u2B55',   // circle
  weather: '\u26C5',      // cloud
  vessel_position: '\u2693', // anchor
  flight_position: '\u2708', // plane
  cyber_incident: '\uD83D\uDEE1', // shield
  economic: '\uD83D\uDCC8', // chart
  sanction: '\uD83D\uDEAB', // prohibited
};

export class DetailPanel {
  private container: HTMLElement;
  private contentArea: HTMLElement;
  private cleanupFns: (() => void)[] = [];
  private severityBadge: HTMLElement | null = null;
  private currentEventId: string | null = null;

  constructor() {
    this.container = document.createElement('div');
    this.container.className = 'detail-panel';
    this.container.style.cssText = 'display: flex; flex-direction: column; height: 100%; overflow-y: auto;';

    this.contentArea = document.createElement('div');
    this.contentArea.style.cssText = 'padding: 12px;';
    this.container.appendChild(this.contentArea);

    // Pre-load population density grid
    preloadPopulationGrid();

    this.renderEmpty();

    // Listen for event selection
    this.cleanupFns.push(
      eventBus.on('event:selected', (event) => {
        this.render(event);
      }),
    );

    // Listen for focal point selection
    this.cleanupFns.push(
      eventBus.on('focal-point:selected', (fp) => {
        this.renderFocalPoint(fp);
      }),
    );

    // Listen for async classification updates — update category badge color
    this.cleanupFns.push(
      eventBus.on('ai:classify:complete', ({ eventId, result }) => {
        if (eventId === this.currentEventId && this.severityBadge) {
          // ClassifierResult provides category, not severity — map high-confidence
          // classifications to a visual indicator on the existing badge
          if (result.confidence >= 0.8) {
            this.severityBadge.title = `AI: ${result.category} (${Math.round(result.confidence * 100)}%)`;
          }
        }
      }),
    );
  }

  getElement(): HTMLElement {
    return this.container;
  }

  destroy(): void {
    for (const fn of this.cleanupFns) fn();
    this.cleanupFns = [];
  }

  /** Render empty state. */
  private renderEmpty(): void {
    this.contentArea.textContent = '';
    const msg = document.createElement('div');
    msg.style.cssText = 'padding: 32px 16px; text-align: center; color: var(--text-muted); font-size: 12px;';
    msg.textContent = 'Select an event to view details';
    this.contentArea.appendChild(msg);
  }

  /** Render full event detail. */
  private render(event: NormalizedEvent): void {
    this.contentArea.textContent = '';
    this.currentEventId = event.id;

    // --- Header: title + severity badge ---
    const header = document.createElement('div');
    header.style.cssText = 'margin-bottom: 12px;';

    const severity = event.severity ?? 'info';

    const titleRow = document.createElement('div');
    titleRow.style.cssText = 'display: flex; align-items: flex-start; gap: 8px;';

    const title = document.createElement('h3');
    title.style.cssText = 'margin: 0; font-size: 14px; font-weight: 600; flex: 1; color: var(--text-primary); line-height: 1.3;';
    title.textContent = event.title;
    titleRow.appendChild(title);

    const badge = document.createElement('span');
    badge.style.cssText = `
      font-size: 10px; font-weight: 700; text-transform: uppercase;
      padding: 2px 8px; border-radius: 10px; white-space: nowrap;
      color: var(--bg-primary); background: ${SEVERITY_COLORS[severity] ?? SEVERITY_COLORS['info']};
    `;
    badge.textContent = severity.toUpperCase();
    titleRow.appendChild(badge);
    this.severityBadge = badge;

    header.appendChild(titleRow);
    this.contentArea.appendChild(header);

    // --- Meta row: type, source, timestamp ---
    const meta = document.createElement('div');
    meta.style.cssText = `
      display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
      font-size: 11px; color: var(--text-secondary); margin-bottom: 12px;
      padding-bottom: 10px; border-bottom: 1px solid var(--border-primary);
    `;

    const typeIcon = TYPE_ICONS[event.type] ?? '\u2022';
    const typeSpan = document.createElement('span');
    typeSpan.textContent = `${typeIcon} ${event.type.replace(/_/g, ' ')}`;
    meta.appendChild(typeSpan);

    const dot1 = document.createElement('span');
    dot1.textContent = '\u00B7';
    dot1.style.color = 'var(--text-muted)';
    meta.appendChild(dot1);

    const sourceSpan = document.createElement('span');
    sourceSpan.textContent = event.source;
    meta.appendChild(sourceSpan);

    const dot2 = document.createElement('span');
    dot2.textContent = '\u00B7';
    dot2.style.color = 'var(--text-muted)';
    meta.appendChild(dot2);

    const timeSpan = document.createElement('span');
    timeSpan.title = formatUTC(event.timestamp);
    timeSpan.textContent = `${formatUTC(event.timestamp)} (${relativeTime(event.timestamp)})`;
    meta.appendChild(timeSpan);

    this.contentArea.appendChild(meta);

    // --- Description ---
    if (event.description) {
      const desc = document.createElement('div');
      desc.style.cssText = 'font-size: 12px; color: var(--text-primary); line-height: 1.5; margin-bottom: 12px;';
      desc.textContent = event.description;
      this.contentArea.appendChild(desc);
    }

    // --- Summarize with AI ---
    this.contentArea.appendChild(this.buildSummarizeSection(event));

    // --- Location ---
    if (event.location) {
      const loc = event.location;
      const locSection = document.createElement('div');
      locSection.style.cssText = `
        margin-bottom: 12px; padding: 8px 10px;
        background: var(--bg-secondary); border-radius: 6px;
        font-size: 12px;
      `;

      const locHeader = document.createElement('div');
      locHeader.style.cssText = 'display: flex; align-items: center; justify-content: space-between;';

      const locText = document.createElement('span');
      locText.style.color = 'var(--text-primary)';
      const name = loc.name ?? '';
      const coords = `(${loc.lat.toFixed(4)}, ${loc.lng.toFixed(4)})`;
      locText.textContent = name ? `${name} ${coords}` : coords;
      locHeader.appendChild(locText);

      const flyBtn = document.createElement('button');
      flyBtn.style.cssText = `
        font-size: 10px; padding: 2px 8px; border: 1px solid var(--border-primary);
        border-radius: 4px; background: transparent; color: var(--text-secondary);
        cursor: pointer;
      `;
      flyBtn.textContent = 'Fly to';
      flyBtn.onclick = () => {
        const map = getMap();
        if (map && loc) {
          map.flyTo({
            center: [loc.lng, loc.lat],
            zoom: Math.max(map.getZoom(), 8),
            duration: 800,
          });
        }
      };
      locHeader.appendChild(flyBtn);

      locSection.appendChild(locHeader);

      // Population exposure estimate (rendered below coordinates)
      const popRow = document.createElement('div');
      popRow.style.cssText = 'margin-top: 6px; font-size: 11px; color: var(--text-muted);';
      popRow.textContent = 'Estimating population exposure...';
      locSection.appendChild(popRow);

      calculatePopulationExposure(loc.lat, loc.lng, event.severity).then(
        (result) => {
          popRow.style.color = 'var(--text-secondary)';
          popRow.textContent = result.population > 0
            ? `\u{1F465} ${result.display}`
            : result.display;
        },
        () => {
          popRow.textContent = 'Population estimate unavailable';
        },
      );

      this.contentArea.appendChild(locSection);
    }

    // --- Tags ---
    if (event.tags && event.tags.length > 0) {
      const tagsContainer = document.createElement('div');
      tagsContainer.style.cssText = 'display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 12px;';

      for (const tag of event.tags) {
        const chip = document.createElement('span');
        chip.style.cssText = `
          font-size: 10px; padding: 2px 8px; border-radius: 10px;
          background: var(--bg-secondary); color: var(--text-secondary);
          border: 1px solid var(--border-primary);
        `;
        chip.textContent = tag;
        tagsContainer.appendChild(chip);
      }

      this.contentArea.appendChild(tagsContainer);
    }

    // --- Links ---
    if (event.links && event.links.length > 0) {
      const linksSection = document.createElement('div');
      linksSection.style.cssText = 'margin-bottom: 12px;';

      const linksHeader = document.createElement('div');
      linksHeader.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.05em;';
      linksHeader.textContent = 'Sources';
      linksSection.appendChild(linksHeader);

      for (const link of event.links) {
        const safeUrl = sanitizeUrl(link.url);
        if (!safeUrl) continue;

        const anchor = document.createElement('a');
        anchor.href = safeUrl;
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        anchor.style.cssText = 'display: block; font-size: 12px; color: var(--accent); text-decoration: none; margin-bottom: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;';
        anchor.textContent = link.label || link.url;
        linksSection.appendChild(anchor);
      }

      this.contentArea.appendChild(linksSection);
    }

    // --- Entities stub (Phase 2) ---
    const entitiesSection = document.createElement('div');
    entitiesSection.style.cssText = 'margin-bottom: 12px;';

    const entitiesHeader = document.createElement('div');
    entitiesHeader.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.05em;';
    entitiesHeader.textContent = 'Linked Entities';
    entitiesSection.appendChild(entitiesHeader);

    const entitiesPlaceholder = document.createElement('div');
    entitiesPlaceholder.style.cssText = 'font-size: 11px; color: var(--text-muted); font-style: italic;';
    entitiesPlaceholder.textContent = 'No entities linked (Phase 2)';
    entitiesSection.appendChild(entitiesPlaceholder);

    this.contentArea.appendChild(entitiesSection);

    // --- Nearby Infrastructure ---
    if (event.location) {
      const infraSection = this.buildNearbyInfrastructure(event.location.lat, event.location.lng);
      this.contentArea.appendChild(infraSection);
    }

    // --- Related Events (within 50km and 24h) ---
    if (event.location) {
      const relatedSection = this.buildRelatedEvents(event);
      this.contentArea.appendChild(relatedSection);
    }
  }

  /** Render focal point detail view — shows real intelligence, not a generic event. */
  private renderFocalPoint(fp: FocalPoint): void {
    this.contentArea.textContent = '';
    this.currentEventId = fp.id;

    // --- Header: entity name + urgency badge + score ---
    const header = document.createElement('div');
    header.style.cssText = 'margin-bottom: 12px;';

    const titleRow = document.createElement('div');
    titleRow.style.cssText = 'display: flex; align-items: center; gap: 8px;';

    const title = document.createElement('h3');
    title.style.cssText = 'margin: 0; font-size: 16px; font-weight: 700; flex: 1; color: var(--text-primary);';
    title.textContent = fp.entity;
    titleRow.appendChild(title);

    const badge = document.createElement('span');
    const badgeColor = fp.urgencyLevel === 'critical' ? 'var(--severity-critical)'
      : fp.urgencyLevel === 'elevated' ? 'var(--severity-high)' : 'var(--severity-medium)';
    badge.style.cssText = `
      font-size: 10px; font-weight: 700; text-transform: uppercase;
      padding: 2px 8px; border-radius: 10px; white-space: nowrap;
      color: var(--bg-primary); background: ${badgeColor};
    `;
    badge.textContent = fp.urgencyLevel.toUpperCase();
    titleRow.appendChild(badge);

    const scoreEl = document.createElement('span');
    scoreEl.style.cssText = 'font-size: 14px; font-weight: 700; color: var(--text-secondary);';
    scoreEl.textContent = String(fp.score);
    titleRow.appendChild(scoreEl);

    header.appendChild(titleRow);
    this.contentArea.appendChild(header);

    // --- Lead headline — most severe signal ---
    if (fp.signals.length > 0) {
      const lead = document.createElement('div');
      lead.style.cssText = `
        font-size: 13px; color: var(--text-primary); line-height: 1.5;
        margin-bottom: 12px; padding-bottom: 10px;
        border-bottom: 1px solid var(--border-primary);
      `;
      lead.textContent = fp.signals[0].description;
      this.contentArea.appendChild(lead);
    }

    // --- Signal breakdown: grouped by type with counts ---
    const typeCounts: Record<string, number> = {};
    for (const s of fp.signals) {
      const t = s.type.replace(/_/g, ' ');
      typeCounts[t] = (typeCounts[t] ?? 0) + 1;
    }
    const breakdownText = Object.entries(typeCounts)
      .map(([t, c]) => `${c} ${t}`)
      .join(' \u00b7 ');

    if (breakdownText) {
      const breakdown = document.createElement('div');
      breakdown.style.cssText = `
        font-size: 12px; color: var(--text-secondary); margin-bottom: 12px;
        padding: 6px 10px; background: var(--bg-secondary); border-radius: 6px;
      `;
      breakdown.textContent = breakdownText;
      this.contentArea.appendChild(breakdown);
    }

    // --- Score breakdown ---
    const scores = document.createElement('div');
    scores.style.cssText = `
      display: flex; gap: 12px; margin-bottom: 12px;
      font-size: 11px; color: var(--text-muted);
    `;
    scores.innerHTML = '';
    for (const [label, value] of [
      ['News', fp.newsScore],
      ['Signal', fp.signalScore],
      ['Correlation', fp.correlationBonus],
      ['Mentions', fp.mentionCount],
    ] as [string, number][]) {
      const item = document.createElement('span');
      item.textContent = `${label}: ${value}`;
      scores.appendChild(item);
    }
    this.contentArea.appendChild(scores);

    // --- Source diversity ---
    const uniqueSources = new Set(fp.signals.map(s => s.type));
    if (uniqueSources.size > 0) {
      const srcDiv = document.createElement('div');
      srcDiv.style.cssText = `
        font-size: 11px; font-weight: 600; color: var(--text-secondary);
        margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.05em;
      `;
      srcDiv.textContent = `Sources (${uniqueSources.size} types)`;
      this.contentArea.appendChild(srcDiv);
    }

    // --- Contributing events list (scrollable) ---
    const eventsHeader = document.createElement('div');
    eventsHeader.style.cssText = `
      font-size: 11px; font-weight: 600; color: var(--text-secondary);
      margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.05em;
    `;
    eventsHeader.textContent = `Contributing Events (${fp.signals.length})`;
    this.contentArea.appendChild(eventsHeader);

    const eventsList = document.createElement('div');
    eventsList.style.cssText = 'max-height: 400px; overflow-y: auto;';

    for (const signal of fp.signals) {
      const row = document.createElement('div');
      row.style.cssText = `
        display: flex; align-items: flex-start; gap: 8px;
        padding: 6px 8px; margin-bottom: 2px; border-radius: 4px;
        background: var(--bg-secondary); cursor: pointer;
      `;
      row.onmouseenter = () => { row.style.background = 'var(--bg-hover)'; };
      row.onmouseleave = () => { row.style.background = 'var(--bg-secondary)'; };

      // Severity dot
      const dot = document.createElement('span');
      const sevColor = SEVERITY_COLORS[signal.severity] ?? SEVERITY_COLORS['info'];
      dot.style.cssText = `
        width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
        margin-top: 4px; background: ${sevColor};
      `;
      row.appendChild(dot);

      // Content
      const content = document.createElement('div');
      content.style.cssText = 'flex: 1; min-width: 0;';

      const signalTitle = document.createElement('div');
      signalTitle.style.cssText = 'font-size: 12px; color: var(--text-primary); line-height: 1.4; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;';
      signalTitle.textContent = signal.description;
      content.appendChild(signalTitle);

      const signalMeta = document.createElement('div');
      signalMeta.style.cssText = 'font-size: 10px; color: var(--text-muted); margin-top: 2px;';
      signalMeta.textContent = `${signal.type.replace(/_/g, ' ')} \u00b7 ${relativeTime(signal.timestamp)}`;
      content.appendChild(signalMeta);

      row.appendChild(content);
      eventsList.appendChild(row);
    }

    this.contentArea.appendChild(eventsList);

    // --- Investigate link ---
    const invBtn = document.createElement('button');
    invBtn.type = 'button';
    invBtn.style.cssText = `
      font-size: 11px; padding: 8px 12px; border-radius: 4px; cursor: pointer;
      background: var(--accent); color: var(--bg-primary); border: none;
      width: 100%; margin-top: 12px; font-weight: 600;
    `;
    invBtn.textContent = `Investigate "${fp.entity}"`;
    invBtn.onclick = () => {
      eventBus.emit('investigation:request', { query: fp.entity, tier: 'STANDARD' });
    };
    this.contentArea.appendChild(invBtn);
  }

  /** Build "Summarize with AI" section. */
  private buildSummarizeSection(event: NormalizedEvent): HTMLElement {
    const section = document.createElement('div');
    section.style.cssText = 'margin-bottom: 12px;';

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.style.cssText = `
      font-size: 11px; padding: 6px 12px; border-radius: 4px; cursor: pointer;
      background: var(--accent); color: var(--bg-primary); border: none;
      width: 100%;
    `;

    const summaryArea = document.createElement('div');
    summaryArea.style.cssText = 'margin-top: 8px; display: none;';

    const hasProviders = getAvailableProviderIds().length > 0;

    if (!hasProviders) {
      btn.textContent = 'Summarize with AI';
      btn.disabled = true;
      btn.title = 'No AI providers configured';
      btn.style.opacity = '0.5';
      btn.style.cursor = 'not-allowed';
    } else {
      btn.textContent = 'Summarize with AI';
      btn.onclick = async () => {
        btn.disabled = true;
        btn.textContent = 'Summarizing...';

        try {
          const result = await summarizeEvent(event);

          summaryArea.style.display = '';
          summaryArea.textContent = '';

          const text = document.createElement('div');
          text.style.cssText = 'font-size: 12px; color: var(--text-primary); line-height: 1.5;';
          text.textContent = result.summary;
          summaryArea.appendChild(text);

          const attribution = document.createElement('div');
          attribution.style.cssText = 'font-size: 10px; color: var(--text-muted); margin-top: 4px;';
          attribution.textContent = result.cached
            ? `via ${result.provider} (cached)`
            : `via ${result.provider}`;
          summaryArea.appendChild(attribution);

          btn.textContent = 'Summarize with AI';
          btn.style.display = 'none';
        } catch (err) {
          summaryArea.style.display = '';
          summaryArea.textContent = '';

          const errEl = document.createElement('div');
          errEl.style.cssText = 'font-size: 11px; color: var(--accent-red);';
          errEl.textContent = err instanceof Error ? err.message : 'Summarization failed';
          summaryArea.appendChild(errEl);

          btn.disabled = false;
          btn.textContent = 'Summarize with AI';
        }
      };
    }

    section.appendChild(btn);
    section.appendChild(summaryArea);
    return section;
  }

  /** Build "Nearby Infrastructure" section — assets within 100 km. */
  private buildNearbyInfrastructure(lat: number, lng: number): HTMLElement {
    const section = document.createElement('div');
    section.style.cssText = 'margin-bottom: 12px;';

    const sectionHeader = document.createElement('div');
    sectionHeader.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.05em;';
    sectionHeader.textContent = 'Nearby Infrastructure';
    section.appendChild(sectionHeader);

    const assets = findRelatedAssets(lat, lng);

    if (assets.length === 0) {
      const none = document.createElement('div');
      none.style.cssText = 'font-size: 11px; color: var(--text-muted); font-style: italic;';
      none.textContent = 'No infrastructure within 100 km';
      section.appendChild(none);
      return section;
    }

    // Show up to 15 nearest assets
    const display = assets.slice(0, 15);

    for (const asset of display) {
      section.appendChild(this.buildAssetRow(asset));
    }

    if (assets.length > 15) {
      const more = document.createElement('div');
      more.style.cssText = 'font-size: 10px; color: var(--text-muted); margin-top: 4px;';
      more.textContent = `+${assets.length - 15} more assets within 100 km`;
      section.appendChild(more);
    }

    return section;
  }

  /** Render a single nearby-asset row. */
  private buildAssetRow(asset: NearbyAsset): HTMLElement {
    const row = document.createElement('div');
    row.style.cssText = `
      display: flex; align-items: center; gap: 6px;
      padding: 4px 8px; margin-bottom: 2px; border-radius: 4px;
      font-size: 11px; color: var(--text-primary);
      background: var(--bg-secondary);
    `;

    // Category badge
    const badge = document.createElement('span');
    badge.style.cssText = `
      font-size: 9px; font-weight: 600; text-transform: uppercase;
      padding: 1px 5px; border-radius: 3px; white-space: nowrap;
      background: var(--border-primary); color: var(--text-secondary);
    `;
    badge.textContent = ASSET_CATEGORY_LABELS[asset.type] ?? asset.type;
    row.appendChild(badge);

    // Name
    const name = document.createElement('span');
    name.style.cssText = 'flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;';
    name.textContent = asset.name;
    name.title = asset.detail ?? asset.name;
    row.appendChild(name);

    // Distance + bearing
    const dist = document.createElement('span');
    dist.style.cssText = 'font-size: 10px; color: var(--text-muted); white-space: nowrap;';
    dist.textContent = `${asset.distanceKm} km ${asset.compass}`;
    row.appendChild(dist);

    return row;
  }

  /** Find and render related events within 50km and 24h. */
  private buildRelatedEvents(event: NormalizedEvent): HTMLElement {
    const section = document.createElement('div');

    const sectionHeader = document.createElement('div');
    sectionHeader.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.05em;';
    sectionHeader.textContent = 'Related Events';
    section.appendChild(sectionHeader);

    const loc = event.location!;
    const eventTime = new Date(event.timestamp).getTime();
    const dayMs = 24 * 60 * 60 * 1000;

    const allEvents = appStore.getState().events;
    const related = allEvents.filter((e) => {
      if (e.id === event.id) return false;
      if (!e.location) return false;
      const timeDiff = Math.abs(new Date(e.timestamp).getTime() - eventTime);
      if (timeDiff > dayMs) return false;
      const dist = haversineDistance(loc.lat, loc.lng, e.location.lat, e.location.lng);
      return dist <= 50;
    });

    // Sort by time descending, limit to 10
    related.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    const display = related.slice(0, 10);

    if (display.length === 0) {
      const none = document.createElement('div');
      none.style.cssText = 'font-size: 11px; color: var(--text-muted); font-style: italic;';
      none.textContent = 'No related events nearby';
      section.appendChild(none);
      return section;
    }

    for (const rel of display) {
      section.appendChild(createCompactEventItem(rel));
    }

    return section;
  }

}

/** Factory: create the detail panel element. */
export function createDetailPanel(): HTMLElement {
  const panel = new DetailPanel();
  const el = panel.getElement();
  (el as HTMLElement & { destroy?: () => void }).destroy = () => panel.destroy();
  return el;
}
